/**
 * Child Card Component
 * Displays a child's information with edit/delete actions
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Edit, Trash2, Phone, Mail, AlertCircle, Cake, AlertTriangle, X, FileWarning, User, Camera, Printer, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { calculateAge } from "@/utils/helpers";
import axios from "axios";
import { toast } from "sonner";
import { usePhotoRenewal } from "@/hooks/usePhotoRenewal";
import { PLANNER_CATEGORIES } from "@/constants/plannerCategories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Child Photo Avatar component with error handling + click-to-preview
const ChildPhotoAvatar = ({ child, onEdit, size = "sm" }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const { src: photoSrc, failed, handleError } = usePhotoRenewal(child.id, child.photoUrl);

  const sizeClasses = size === "sm" 
    ? "w-16 h-16" 
    : "w-16 h-16";
  
  const iconSize = size === "sm" ? "w-6 h-6" : "w-6 h-6";
  
  // Category-aware colors for the avatar ring
  const colors = categoryColors[child.category] || categoryColors.default;
  
  // Show camera if no photoUrl OR if image failed to load after bounded retries
  const hasValidPhoto = !!photoSrc && !failed;
  
  const handleClick = () => {
    if (hasValidPhoto) {
      setPreviewOpen(true);
    } else {
      onEdit(child);
    }
  };
  
  return (
    <>
    <button
      onClick={handleClick}
      className="relative group flex-shrink-0"
      title={hasValidPhoto ? "Click to view photo" : "Click to add photo (will show on Daily Sign In)"}
      data-testid={`child-photo-avatar-${child.id}`}
    >
      {hasValidPhoto ? (
        <img
          src={photoSrc}
          alt={child.name}
          className={`${sizeClasses} rounded-full object-cover border-2 ${colors.border} group-hover:opacity-80 transition-colors`}
          onError={handleError}
          onLoad={(e) => { if (e.target.naturalWidth <= 2 || e.target.naturalHeight <= 2) handleError(); }}
        />
      ) : (
        <div className={`${sizeClasses} rounded-full ${colors.bg} border-2 border-dashed ${colors.border} flex items-center justify-center group-hover:opacity-80 transition-colors`}>
          <Camera className={`${iconSize} ${colors.text} opacity-60`} />
        </div>
      )}
      {/* Hover overlay */}
      <div className={`absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center`}>
        <Camera className={`${iconSize} text-white`} />
      </div>
    </button>
    {hasValidPhoto && (
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-sm" data-testid="child-photo-preview-dialog">
          <DialogHeader>
            <DialogTitle>{child.name}</DialogTitle>
          </DialogHeader>
          <img
            src={photoSrc}
            alt={child.name}
            className="w-full max-h-[70vh] object-contain rounded-lg"
            data-testid="child-photo-preview-image"
            onError={handleError}
          />
          <DialogFooter className="flex-row justify-end gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="child-photo-preview-close-btn">
              Close
            </Button>
            <Button
              onClick={() => { setPreviewOpen(false); onEdit(child); }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="child-photo-preview-replace-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Replace Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

// Format phone number to (XXX) XXX-XXXX
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-digits
  const digits = String(phone).replace(/\D/g, '');
  // Format based on length
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return phone; // Return original if can't format
};

const categoryColors = {
  full_time: { border: "border-[#2F6DB5]/30", text: "text-[#2F6DB5]", bg: "bg-[#2F6DB5]/5", label: "Full Time" },
  regular: { border: "border-[#2F6DB5]/30", text: "text-[#2F6DB5]", bg: "bg-[#2F6DB5]/5", label: "Full Time" },
  part_time: { border: "border-[#E83E8C]/30", text: "text-[#E83E8C]", bg: "bg-[#E83E8C]/5", label: "Part Time" },
  school_age: { border: "border-[#28A745]/30", text: "text-[#28A745]", bg: "bg-[#28A745]/5", label: "School Age" },
  after_school: { border: "border-[#E83E8C]/30", text: "text-[#E83E8C]", bg: "bg-[#E83E8C]/5", label: "After School" },
  default: { border: "border-[#64748B]/30", text: "text-[#64748B]", bg: "bg-[#64748B]/5", label: "Uncategorized" }
};

// Age group display mapping
const ageGroupLabels = {
  'prek': { label: 'PreK', color: 'bg-[#2F6DB5]/10 border-[#2F6DB5]/30 text-[#2F6DB5]' },
  'older_toddler': { label: 'Older Toddler', color: 'bg-[#4DA3D9]/10 border-[#4DA3D9]/30 text-[#4DA3D9]' },
  'young_toddler': { label: 'Young Toddler', color: 'bg-[#E83E8C]/10 border-[#E83E8C]/30 text-[#E83E8C]' },
  'infant': { label: 'Infant', color: 'bg-[#F4A623]/10 border-[#F4A623]/30 text-[#F4A623]' }
};

export const ChildCard = ({
  child,
  onEdit,
  onDelete,
  onMismatchCleared,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
  canEdit = false,
  pendingApprovals = [],
  isStillHere = false,
  schoolsList = null,
  plannerRooms = []
}) => {
  const [showMismatchDetails, setShowMismatchDetails] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  
  const colors = categoryColors[child.category] || categoryColors.default;
  const ageGroup = ageGroupLabels[child.fullTimeSubcategory];
  const isFullTime = child.category === 'full_time' || child.category === 'regular';
  const canEditDelete = canEdit;
  const hasMismatch = child.mismatchInfo?.hasMismatch;
  // "Room for Ratios" badge — only shown when this child actually has an override set, so
  // at a glance you can tell their ratio is being counted in a different room than their
  // own Category/Age Group would normally put them in.
  const plannerRoomOverride = child.plannerRoomOverride
    ? plannerRooms.find((r) => r.id === child.plannerRoomOverride)
    : null;
  // "School Needed" — School Age / After School child with no school, or a
  // school that doesn't match the director-maintained list (e.g. imported
  // free text that was never matched). Only computed once schoolsList has
  // successfully loaded, so a transient fetch failure never falsely flags
  // every child in these categories.
  const isSchoolCategory = child.category === 'school_age' || child.category === 'after_school';
  const schoolMatches = !!child.school && Array.isArray(schoolsList) &&
    schoolsList.some((s) => s.name.trim().toLowerCase() === child.school.trim().toLowerCase());
  const needsSchool = isSchoolCategory && Array.isArray(schoolsList) && !schoolMatches;

  const handlePrintChild = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const pickup = (child.pickupPersons || []).filter(p => p.name);
    const emergency = (child.emergencyContacts || []).filter(c => c.name);
    const fmt = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-US'); } catch(e) { return d; } };
    w.document.write(`<!DOCTYPE html><html><head><title>Child Record - ${child.name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',system-ui,sans-serif;color:#1a1a1a;padding:40px 50px;max-width:850px;margin:0 auto;font-size:13px;line-height:1.4}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1e3a5f;padding-bottom:16px;margin-bottom:24px}
.header h1{font-size:22px;font-weight:700;color:#1e3a5f;letter-spacing:-0.5px}
.header .subtitle{font-size:12px;color:#6b7280;margin-top:2px}
.logo{height:50px;width:auto;margin-bottom:8px}
.section{margin-bottom:20px}
.section-title{font-size:13px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-bottom:10px}
.grid{display:grid;gap:8px 24px}
.grid-2{grid-template-columns:1fr 1fr}
.grid-3{grid-template-columns:1fr 1fr 1fr}
.field .label{font-size:10px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.3px}
.field .value{font-size:13px;color:#111827;font-weight:500;margin-top:1px}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:6px}
th{background:#f8fafc;color:#374151;font-weight:600;text-align:left;padding:8px 10px;border-bottom:2px solid #e5e7eb}
td{padding:7px 10px;border-bottom:1px solid #f3f4f6}
.footer{margin-top:30px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center}
.badge{display:inline-block;padding:2px 8px;border-radius:3px;font-size:11px;font-weight:600}
@media print{body{padding:20px 30px}@page{margin:0.5in}}
</style></head><body>
<div class="header">
  <div><img src="/simple-sign-in-logo.png" class="logo" alt="Simple Sign In" onerror="this.style.display='none'" /><h1>${child.name}</h1><div class="subtitle">Child Enrollment Record</div></div>
  <div style="text-align:right"><div class="subtitle">${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</div></div>
</div>
<div class="section">
  <div class="section-title">Child Information</div>
  <div class="grid grid-3">
    <div class="field"><div class="label">Date of Birth</div><div class="value">${fmt(child.birthdate)}</div></div>
    <div class="field"><div class="label">Age</div><div class="value">${child.birthdate ? calculateAge(child.birthdate) : '-'}</div></div>
    <div class="field"><div class="label">Category</div><div class="value">${(child.category || '').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</div></div>
    <div class="field"><div class="label">Enrollment Date</div><div class="value">${fmt(child.startDate)}</div></div>
    <div class="field"><div class="label">Allergies</div><div class="value">${child.allergies || 'None noted'}</div></div>
    <div class="field"><div class="label">Medical Notes</div><div class="value">${child.medicalNotes || 'None'}</div></div>
  </div>
</div>
${pickup.length > 0 ? `<div class="section">
  <div class="section-title">Parents</div>
  <table>
    <thead><tr><th>Role</th><th>Name</th><th>Phone</th><th>Email</th><th>Relationship</th><th>Restrictions</th></tr></thead>
    <tbody>${pickup.slice(0,2).map((p, i) => `<tr><td style="color:#6b7280;font-size:11px">${i === 0 ? 'Parent 1' : 'Parent 2'}</td><td style="font-weight:600">${p.name}</td><td>${p.phone || '-'}</td><td>${p.email || '-'}</td><td>${p.relationship || '-'}</td><td>${p.restrictions || 'None'}</td></tr>`).join('')}</tbody>
  </table>
</div>` : ''}
${pickup.slice(2).length > 0 ? `<div class="section">
  <div class="section-title">Guardians</div>
  <table>
    <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Relationship</th><th>Restrictions</th></tr></thead>
    <tbody>${pickup.slice(2).map(p => `<tr><td style="font-weight:600">${p.name}</td><td>${p.phone || '-'}</td><td>${p.email || '-'}</td><td>${p.relationship || '-'}</td><td>${p.restrictions || 'None'}</td></tr>`).join('')}</tbody>
  </table>
</div>` : ''}
${emergency.length > 0 ? `<div class="section">
  <div class="section-title">Emergency Contacts (Call Only)</div>
  <table>
    <thead><tr><th>Name</th><th>Phone</th></tr></thead>
    <tbody>${emergency.map(c => `<tr><td style="font-weight:600">${c.name}</td><td>${c.phone || '-'}</td></tr>`).join('')}</tbody>
  </table>
</div>` : ''}
${child.notes ? `<div class="section"><div class="section-title">Notes</div><p style="color:#374151">${child.notes}</p></div>` : ''}
<div class="footer">Confidential Child Record &mdash; Printed ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</div>
<script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  const handleClearMismatch = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/children/${child.id}/mismatch`);
      toast.success("Mismatch cleared - template now matches");
      setShowClearConfirm(false);
      setShowMismatchDetails(false);
      if (onMismatchCleared) {
        onMismatchCleared(child.id);
      }
    } catch (error) {
      console.error("Error clearing mismatch:", error);
      toast.error("Failed to clear mismatch");
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
    <Card className={`bg-white/80 backdrop-blur-sm border-2 ${hasMismatch ? 'border-yellow-400' : colors.border}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {selectMode && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect(child.id)}
                className="mt-1"
                data-testid={`select-child-${child.id}`}
              />
            )}
            {/* Child Photo - Clickable to edit */}
            <ChildPhotoAvatar 
              child={child} 
              onEdit={onEdit}
              size="sm"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <CardTitle className={`text-xl ${colors.text}`}>{child.name}</CardTitle>
                {/* Mismatch Indicator */}
                {hasMismatch && (
                  <button
                    onClick={() => setShowMismatchDetails(true)}
                    className="flex items-center gap-1 px-2 py-1 bg-yellow-100 border border-yellow-400 rounded-full text-yellow-800 text-xs font-medium hover:bg-yellow-200 transition-colors cursor-pointer"
                    title="Click to view mismatch details"
                  >
                    <FileWarning className="w-3 h-3" />
                    Template Mismatch
                  </button>
                )}
                {/* Awaiting Approval Badge — shown only when form_documents.pendingVersion genuinely exists for this child */}
                {pendingApprovals.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    {pendingApprovals.map(p => (
                      <span
                        key={p.formId}
                        className="flex items-center gap-1 pl-2 pr-2 py-1 bg-amber-100 border border-amber-400 rounded-full text-amber-800 text-xs font-medium"
                        title={`${p.formName} is awaiting director approval — go to Records Management to review the submission, enter the Date on Form, and click Approve.`}
                        data-testid={`awaiting-approval-badge-${child.id}-${p.formId}`}
                      >
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        {p.formName} Awaiting Approval
                      </span>
                    ))}
                  </div>
                )}
                {/* Still Here Reminder Badge — child is past a configured Still Here Reminders time and still signed in */}
                {isStillHere && (
                  <span
                    className="flex items-center gap-1 pl-2 pr-2 py-1 bg-purple-100 border border-purple-400 rounded-full text-purple-800 text-xs font-medium"
                    title="This child is past a Still Here Reminders time and is still signed in — a reminder has been sent to their pickup contact."
                    data-testid={`still-here-badge-${child.id}`}
                  >
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    Still Here
                  </span>
                )}
              </div>
              <div className="flex items-start gap-2 mt-1 flex-wrap">
                <span className={`text-sm font-bold ${colors.text} px-2 py-0.5 rounded ${colors.bg}`}>{colors.label}</span>
                <div className="flex flex-col items-start gap-1">
                  {/* Age Group Badge - Only for Full Time children */}
                  {isFullTime && ageGroup && (
                    <Badge className={`text-xs font-semibold ${ageGroup.color}`}>
                      {ageGroup.label}
                    </Badge>
                  )}
                  {/* Show "No Age Group" warning for Full Time without subcategory */}
                  {isFullTime && !ageGroup && (
                    <Badge variant="outline" className="text-xs bg-orange-50 border-orange-300 text-orange-600">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      No Age Group
                    </Badge>
                  )}
                  {plannerRoomOverride && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-teal-50 border-teal-300 text-teal-700"
                      title="This child's Tour & Ratio Planner headcount is counted under this category instead of their own Category/Age Group — their enrollment is unaffected."
                      data-testid={`planner-room-override-badge-${child.id}`}
                    >
                      Assigned to: {PLANNER_CATEGORIES.find((c) => c.value === plannerRoomOverride.category)?.label || plannerRoomOverride.category}
                    </Badge>
                  )}
                </div>
                {child.school && (
                  <Badge variant="outline" className="text-xs">
                    {child.school}
                  </Badge>
                )}
                {needsSchool && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-red-50 border-red-300 text-red-700"
                    title="Select their school on Edit Child so they connect to the correct bus pickup and drop-off records."
                    data-testid={`school-needed-badge-${child.id}`}
                  >
                    <AlertCircle className="w-3 h-3 mr-1" />
                    School Needed
                  </Badge>
                )}
                {child.birthdate && (
                  <Badge variant="outline" className="text-xs bg-pink-50 border-pink-200 text-pink-700">
                    <Cake className="w-3 h-3 mr-1" />
                    {new Date(child.birthdate + 'T00:00:00').toLocaleDateString()} ({calculateAge(child.birthdate)})
                  </Badge>
                )}
                {child.startDate && (
                  <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                    Start: {new Date(child.startDate + 'T00:00:00').toLocaleDateString()}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {canEditDelete && (
            <div className="flex gap-2">
              <Button
                onClick={() => onEdit(child)}
                variant="outline"
                size="sm"
                className={`${colors.border} ${colors.text}`}
                data-testid={`edit-child-${child.id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                onClick={handlePrintChild}
                variant="outline"
                size="sm"
                className="border-gray-300 text-gray-600"
                title="Print Record"
                data-testid={`print-child-${child.id}`}
              >
                <Printer className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => onDelete(child)}
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700"
                title="Move to Deleted"
                data-testid={`delete-child-${child.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* LEFT — Authorized Pickup */}
          <div className="space-y-2">
            <p className={`text-sm font-semibold ${colors.text}`}>Authorized Pickup:</p>
            {child.pickupPersons && child.pickupPersons.filter(p => p.name).length > 0 ? (
              child.pickupPersons.filter(p => p.name).map((person, index) => (
                <div key={index} className={`flex flex-col text-sm ${colors.text} ${colors.bg} p-2 rounded`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{person.name}</span>
                    {person.phone && (
                      <span className="flex items-center gap-1 opacity-70">
                        <Phone className="w-3 h-3" />
                        {formatPhoneNumber(person.phone)}
                      </span>
                    )}
                    {person.email && (
                      <span className="flex items-center gap-1 opacity-70">
                        <Mail className="w-3 h-3" />
                        {person.email}
                      </span>
                    )}
                  </div>
                  {person.restrictions && (
                    <span className="text-xs text-red-600 mt-1">
                      Note: {person.restrictions}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No pickup persons added</p>
            )}
          </div>

          {/* RIGHT — Emergency Contacts (Call Only) */}
          <div className="space-y-2">
            <p className={`text-sm font-semibold ${colors.text}`}>Emergency Contacts (Call Only):</p>
            {child.emergencyContacts && child.emergencyContacts.filter(c => c.name).length > 0 ? (
              child.emergencyContacts.filter(c => c.name).map((contact, index) => (
                <div key={index} className={`flex flex-col text-sm ${colors.text} ${colors.bg} p-2 rounded`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{contact.name}</span>
                    {contact.phone && (
                      <span className="flex items-center gap-1 opacity-70">
                        <Phone className="w-3 h-3" />
                        {formatPhoneNumber(contact.phone)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No emergency contacts added</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Mismatch Details Dialog */}
    <AlertDialog open={showMismatchDetails} onOpenChange={setShowMismatchDetails}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-yellow-800">
            <FileWarning className="w-5 h-5" />
            Template Mismatch for {child.name}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p className="text-gray-600">
                The Excel template has different information than what's saved in the app. 
                The app data has been preserved.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="font-semibold text-yellow-800 mb-2">Differences Found:</p>
                <ul className="space-y-1 text-sm text-yellow-900">
                  {child.mismatchInfo?.mismatchDetails?.map((detail, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-gray-500">
                Please update your Excel template to match the correct information in the app.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <Button
            onClick={() => setShowClearConfirm(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Mismatch
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Confirm Clear Dialog */}
    <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            By clearing this mismatch, you confirm that you have updated the Excel template 
            to match the information in the app. The mismatch indicator will be removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={clearing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleClearMismatch}
            disabled={clearing}
            className="bg-green-600 hover:bg-green-700"
          >
            {clearing ? "Clearing..." : "Yes, Template Now Matches"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default ChildCard;
